import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Trash2, Save, X, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY_FORM = {
  zone: "sidebar_vertical",
  content_kind: "custom",
  linked_entity_id: "",
  linked_entity_slug: "",
  title: "",
  subtitle: "",
  image_url: "",
  cta_label: "",
  cta_url: "",
  badge: "none",
  start_date: "",
  end_date: "",
  active: true,
  order: 0,
};

export default function AdminHomeHighlightsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zone, setZone] = useState("sidebar_vertical");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => {
    setLoading(true);
    api.get("/admin/home/highlights")
      .then((res) => setItems(res.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = (z) => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, zone: z });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      zone: item.zone,
      content_kind: item.content_kind || "custom",
      linked_entity_id: item.linked_entity_id || "",
      linked_entity_slug: item.linked_entity_slug || "",
      title: item.title || "",
      subtitle: item.subtitle || "",
      image_url: item.image_url || "",
      cta_label: item.cta_label || "",
      cta_url: item.cta_url || "",
      badge: item.badge || "none",
      start_date: item.start_date || "",
      end_date: item.end_date || "",
      active: item.active !== false,
      order: item.order || 0,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      badge: form.badge === "none" ? null : form.badge,
      order: parseInt(form.order || 0, 10),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      linked_entity_id: form.linked_entity_id || null,
      linked_entity_slug: form.linked_entity_slug || null,
    };
    try {
      if (editing) {
        await api.patch(`/admin/home/highlights/${editing.id}`, payload);
        toast.success("Mise en avant modifiée.");
      } else {
        await api.post("/admin/home/highlights", payload);
        toast.success("Mise en avant créée.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ?")) return;
    try {
      await api.delete(`/admin/home/highlights/${id}`);
      toast.success("Supprimé.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/admin/home/highlights/${item.id}`, { active: !item.active });
      load();
    } catch (e) { toast.error("Échec"); }
  };

  const filtered = items.filter((it) => it.zone === zone);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-4">
        <Link to="/admin" className="text-xs text-muted-foreground hover:text-[hsl(var(--aito-lagoon))] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Retour admin
        </Link>
      </div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl" data-testid="admin-highlights-title">Mise en avant homepage</h1>
          <p className="text-sm text-muted-foreground">Deux zones administrables sur la page d'accueil AITO.PF.</p>
        </div>
      </div>

      <Tabs value={zone} onValueChange={setZone}>
        <TabsList>
          <TabsTrigger value="sidebar_vertical" data-testid="tab-sidebar">Zone verticale (sous JP 2027)</TabsTrigger>
          <TabsTrigger value="bottom_banner" data-testid="tab-bottom">Bande basse</TabsTrigger>
        </TabsList>

        {["sidebar_vertical", "bottom_banner"].map((z) => (
          <TabsContent key={z} value={z} className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {z === "sidebar_vertical"
                  ? "Format portrait, affiché dans la sidebar droite de la home, sous le compte à rebours JP 2027."
                  : "Bande horizontale discrète en pied de page d'accueil."}
              </p>
              <Button onClick={() => openCreate(z)} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid={`btn-create-${z}`}>
                <Plus className="h-4 w-4 mr-1.5" /> Ajouter
              </Button>
            </div>
            {loading ? (
              <p className="text-sm text-muted-foreground">Chargement...</p>
            ) : filtered.length === 0 ? (
              <Card className="p-6 text-center text-sm text-muted-foreground italic">Aucune mise en avant dans cette zone.</Card>
            ) : (
              filtered.map((item) => (
                <Card key={item.id} className="p-3" data-testid={`highlight-${item.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    {item.image_url && (
                      <div className={z === "sidebar_vertical" ? "w-16 h-20 rounded overflow-hidden bg-muted shrink-0" : "w-20 h-16 rounded overflow-hidden bg-muted shrink-0"}>
                        <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="font-semibold text-sm truncate">{item.title || "(sans titre)"}</div>
                        {item.badge && <Badge variant="secondary" className="text-[9px] uppercase">{item.badge === "sponsorise" ? "Sponsorisé" : "Partenaire"}</Badge>}
                        <Badge variant="outline" className="text-[9px]">ordre {item.order}</Badge>
                        {!item.active && <Badge variant="secondary" className="text-[9px] bg-orange-500/10 text-orange-700">Inactif</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{item.subtitle}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {item.start_date || "—"} → {item.end_date || "—"}
                        {item.cta_url && <> · <span className="font-mono">{item.cta_url}</span></>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => toggleActive(item)} title={item.active ? "Désactiver" : "Activer"}>
                        {item.active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEdit(item)} data-testid={`edit-highlight-${item.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => remove(item.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier" : "Créer"} une mise en avant</DialogTitle>
            <DialogDescription>Programmez, activez et priorisez les contenus mis en avant sur la home.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Zone</Label>
                <Select value={form.zone} onValueChange={(v) => setForm((f) => ({ ...f, zone: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sidebar_vertical">Zone verticale (sous JP 2027)</SelectItem>
                    <SelectItem value="bottom_banner">Bande basse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type de contenu</Label>
                <Select value={form.content_kind} onValueChange={(v) => setForm((f) => ({ ...f, content_kind: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Libre / publicité</SelectItem>
                    <SelectItem value="athlete">Fiche athlète</SelectItem>
                    <SelectItem value="coach">Fiche coach</SelectItem>
                    <SelectItem value="club">Fiche club</SelectItem>
                    <SelectItem value="federation">Fiche fédération</SelectItem>
                    <SelectItem value="directory_entry">Fiche annuaire</SelectItem>
                    <SelectItem value="campaign">Campagne</SelectItem>
                    <SelectItem value="news">Actualité</SelectItem>
                    <SelectItem value="event">Événement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Titre</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="input-title" />
            </div>
            <div>
              <Label>Sous-titre</Label>
              <Textarea value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} rows={2} />
            </div>
            <div>
              <Label>Image (URL ou base64)</Label>
              <Input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="https://... ou data:image/..." />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Label CTA</Label>
                <Input value={form.cta_label} onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))} placeholder="Découvrir" />
              </div>
              <div>
                <Label>URL CTA</Label>
                <Input value={form.cta_url} onChange={(e) => setForm((f) => ({ ...f, cta_url: e.target.value }))} placeholder="/annuaire/... ou https://..." />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Badge</Label>
                <Select value={form.badge} onValueChange={(v) => setForm((f) => ({ ...f, badge: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    <SelectItem value="sponsorise">Sponsorisé</SelectItem>
                    <SelectItem value="partenaire">Partenaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date début</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <Label>Date fin</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Ordre d'affichage</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
                <span className="text-sm">Actif</span>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>ID fiche liée (optionnel)</Label>
                <Input value={form.linked_entity_id} onChange={(e) => setForm((f) => ({ ...f, linked_entity_id: e.target.value }))} placeholder="uuid" />
              </div>
              <div>
                <Label>Slug fiche liée (optionnel)</Label>
                <Input value={form.linked_entity_slug} onChange={(e) => setForm((f) => ({ ...f, linked_entity_slug: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}><X className="h-4 w-4 mr-1.5" /> Annuler</Button>
              <Button onClick={save} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="save-highlight">
                <Save className="h-4 w-4 mr-1.5" /> Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
